<script setup lang="ts">
import GaiaHeader from '../../base/GaiaHeader.vue';
import GaiaText from '../../base/GaiaText.vue';
import { gaiaNeutral, gaiaShadow } from '../../themes/tokens';

const props = defineProps({
  /**
   * Centered serif heading, e.g. "Welcome back". Omit on the outcome screens
   * (verify / invite / reset), where GaiaAuthStatus owns the heading and a
   * second one above it would name the same thing twice.
   */
  title: {
    type: String,
    default: undefined,
  },
  /** Centered sub-line, e.g. "Sign in to your workspace.". */
  subtitle: {
    type: String,
    default: undefined,
  },
  /**
   * Reading width. Most auth screens are one narrow column; the ones that ask
   * more of the user are wider (the profile step's two-column grid at 560, the
   * invitation at 440). The card owns this rather than the route: it is a
   * dimension, not a routing concern.
   */
  width: {
    type: String,
    default: '396px',
  },
});

const border = gaiaNeutral.border;
const surface = gaiaNeutral.surface;
const footBorder = gaiaNeutral.borderSubtle;
const shadow = gaiaShadow.authCard;
</script>

<template>
  <!-- Auth card from the Login mock: serif heading, form content (default
       slot), hairline-topped #footer note. The composed shadow has no card
       theme var, so the chrome is self-contained. -->
  <div class="gaia-auth-card" :style="{ maxWidth: props.width }">
    <!-- Above the heading: the funnel's "Step 1 of 2" counter. Its own slot
         rather than part of the title, because it names where you are in a
         flow while the title names the page. -->
    <gaia-text
      v-if="$slots.eyebrow"
      tag="div"
      variant="label"
      size="micro"
      tone="muted"
      :style="{ textAlign: 'center', marginBottom: '10px' }"
    >
      <slot name="eyebrow" />
    </gaia-text>
    <gaia-header
      v-if="props.title"
      :level="1"
      variant="display"
      size="page"
      :style="{ marginBottom: '6px', textAlign: 'center' }"
    >
      {{ props.title }}
    </gaia-header>
    <gaia-text
      v-if="props.subtitle"
      size="body"
      tone="tertiary"
      :style="{ display: 'block', marginBottom: '22px', textAlign: 'center' }"
    >
      {{ props.subtitle }}
    </gaia-text>
    <slot />
    <gaia-text v-if="$slots.footer" class="foot" size="meta" tone="subtle">
      <slot name="footer" />
    </gaia-text>
  </div>
</template>

<style scoped>
.gaia-auth-card {
  /* Centres itself inside whatever column the layout gives it. */
  width: 100%;
  margin: 0 auto;
  background: v-bind(surface);
  border: 1px solid v-bind(border);
  border-radius: 14px;
  padding: 32px 32px 26px;
  box-shadow: v-bind(shadow);
}

.foot {
  display: block;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid v-bind(footBorder);
  line-height: 1.6;
}
</style>
